import React, { useState, useEffect, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import "./PagesCss/CreditManagement.css";
import article from "./Content";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const AnnualFeeValueCalculator = () => {
  const content = article.find((item) => item.id === 1);
  const [cards, setCards] = useState([
    {
      id: Date.now(),
      name: "Chase Sapphire Preferred®",
      issuer: "Chase",
      annualFee: "95",
      signUpBonus: "60000",
      bonusSpendRequirement: "4000",
      bonusTimeframe: "3",
      rewardsRates: {
        dining: "3",
        groceries: "3",
        travel: "5",
        streaming: "3",
        onlineGroceries: "3",
        others: "1"
      },
      pointsValue: "1.25",
      otherBenefits: "150",
      foreignFee: "0",
      travelCredits: "0",
        loungeAccess: "No",
      insuranceBenefits: "250",
      priorityPass: "No",
      credits: {
        dashpass: "0",
        doordash: "0",
        instacart: "0",
        lyft: "0"
      },
      cardType: "Travel",
      creditTier: "Premium"
    },
  ]);

  const [monthlySpending, setMonthlySpending] = useState({
    dining: "400",
    groceries: "800",
    travel: "300",
    gas: "200",
    entertainment: "150",
    shopping: "300",
    streaming: "50",
    onlineGroceries: "200",
    utilities: "350",
    others: "200"
  });

  const [travelProfile, setTravelProfile] = useState({
    internationalTrips: "2",
    domesticTrips: "4",
    hotelNights: "10",
    rentalCarDays: "5",
    foreignSpendingPercent: "25"
  });

  const [results, setResults] = useState(null);
  const [showChart, setShowChart] = useState(true);
  const [activeTab, setActiveTab] = useState("calculator");
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [timeframe, setTimeframe] = useState("first_year"); // first_year, ongoing, lifetime
  const [scenarioComparison, setScenarioComparison] = useState(null);
  const resultsRef = useRef();

  // Auto-calculate when inputs change
  useEffect(() => {
    const hasValidData = cards.some(card => card.annualFee);
    if (hasValidData) {
      setTimeout(() => {
        calculateValue();
      }, 100);
    }
  }, [cards, monthlySpending, travelProfile, timeframe]);

  const addCard = () => {
    const newCard = {
      id: Date.now(),
      name: "American Express Gold Card",
      issuer: "American Express",
      annualFee: "250",
      signUpBonus: "60000",
      bonusSpendRequirement: "4000",
      bonusTimeframe: "6",
      rewardsRates: {
        dining: "4",
        groceries: "4",
        travel: "3",
        streaming: "1",
        onlineGroceries: "1",
        others: "1"
      },
      pointsValue: "1",
      otherBenefits: "240",
      foreignFee: "0",
      travelCredits: "100",
      loungeAccess: "No",
      insuranceBenefits: "150",
      priorityPass: "No",
      credits: {
        dining: "120",
        uber: "120",
        airlineFee: "0",
        hotel: "0"
      },
      cardType: "Dining/Groceries",
      creditTier: "Premium"
    };
    setCards([...cards, newCard]);
    setActiveCardIndex(cards.length);
  };

  const removeCard = (id) => {
    if (cards.length > 1) {
      const newCards = cards.filter(card => card.id !== id);
      setCards(newCards);
      setActiveCardIndex(0);
    }
  };

  const handleCardChange = (id, field, value, subField = null) => {
    const newCards = cards.map(card => {
      if (card.id === id) {
        if (subField) {
          return {
            ...card,
            [field]: {
              ...card[field],
              [subField]: value
            }
          };
        } else {
          return {
            ...card,
            [field]: value
          };
        }
      }
      return card;
    });
    setCards(newCards);
  };

  const handleSpendingChange = (category, value) => {
    setMonthlySpending({ ...monthlySpending, [category]: value });
  };

  const handleTravelProfileChange = (field, value) => {
    setTravelProfile({ ...travelProfile, [field]: value });
  };

  const resetCalculator = () => {
    setCards([
      {
        id: Date.now(),
        name: "Chase Sapphire Preferred®",
        issuer: "Chase",
        annualFee: "95",
        signUpBonus: "60000",
        bonusSpendRequirement: "4000",
        bonusTimeframe: "3",
        rewardsRates: {
          dining: "3",
          groceries: "3",
          travel: "5",
          streaming: "3",
          onlineGroceries: "3",
          others: "1"
        },
        pointsValue: "1.25",
        otherBenefits: "150",
        foreignFee: "0",
        travelCredits: "0",
        loungeAccess: "No",
        insuranceBenefits: "250",
        priorityPass: "No",
        credits: {
          dashpass: "0",
          doordash: "0",
          instacart: "0",
          lyft: "0"
        },
        cardType: "Travel",
        creditTier: "Premium"
      },
    ]);
    setMonthlySpending({
      dining: "400",
      groceries: "800",
      travel: "300",
      gas: "200",
      entertainment: "150",
      shopping: "300",
      streaming: "50",
      onlineGroceries: "200",
      utilities: "350",
      others: "200"
    });
    setTravelProfile({
      internationalTrips: "2",
      domesticTrips: "4",
      hotelNights: "10",
      rentalCarDays: "5",
      foreignSpendingPercent: "25"
    });
    setResults(null);
    setActiveCardIndex(0);
    setTimeframe("first_year");
  };

  const calculateValue = () => {
    const cardResults = cards.map((card) => {
      const annualFee = parseFloat(card.annualFee) || 0;
      
      // Calculate category rewards
      let annualRewards = 0;
      const rewardsBreakdown = {};
      
      Object.entries(monthlySpending).forEach(([category, amount]) => {
        const rate = card.rewardsRates[category] || card.rewardsRates.others || 1;
        const categoryReward = ((parseFloat(amount) || 0) * parseFloat(rate) * 12) / 100;
        rewardsBreakdown[category] = categoryReward;
        annualRewards += categoryReward;
      });

      // Apply points multiplier for premium cards
      const pointsMultiplier = parseFloat(card.pointsValue) || 1;
      const effectiveAnnualRewards = annualRewards * pointsMultiplier;

      // Calculate sign-up bonus value
      const signUpBonus = calculateSignUpBonus(card);
      const effectiveSignUpBonus = signUpBonus * pointsMultiplier;

      // Calculate travel-related savings
      const travelSavings = calculateTravelSavings(card, travelProfile);

      // Calculate credit values
      const creditValue = calculateCreditValue(card);

      // Calculate foreign fee savings
      const foreignFeeSavings = calculateForeignFeeSavings(card, monthlySpending.travel, travelProfile.foreignSpendingPercent);

      // Calculate other benefits value
      const otherBenefits = parseFloat(card.otherBenefits) || 0;
      const insuranceValue = parseFloat(card.insuranceBenefits) || 0;

      // Calculate total benefits based on timeframe
      let totalBenefits;
      let netValue;
      
      if (timeframe === "first_year") {
        totalBenefits = effectiveAnnualRewards + effectiveSignUpBonus + travelSavings + 
                       creditValue + foreignFeeSavings + otherBenefits + insuranceValue;
        netValue = totalBenefits - annualFee;
      } else if (timeframe === "ongoing") {
        totalBenefits = effectiveAnnualRewards + travelSavings + creditValue + 
                       foreignFeeSavings + otherBenefits + insuranceValue;
        netValue = totalBenefits - annualFee;
      } else { // lifetime (5 years)
        totalBenefits = (effectiveAnnualRewards * 5) + effectiveSignUpBonus + 
                       (travelSavings * 5) + (creditValue * 5) + 
                       (foreignFeeSavings * 5) + (otherBenefits * 5) + (insuranceValue * 5);
        netValue = totalBenefits - (annualFee * 5);
      }

      // Calculate break-even spending
      const breakEvenSpending = calculateBreakEvenSpending(card, timeframe);

      // Calculate value rating
      const valueRating = calculateValueRating(netValue, annualFee, timeframe);

      // Generate recommendations
      const recommendations = generateRecommendations(card, netValue, annualFee, timeframe);

      return {
        name: card.name,
        issuer: card.issuer,
        annualFee: annualFee.toFixed(2),
        annualRewards: effectiveAnnualRewards.toFixed(2),
        signUpBonus: effectiveSignUpBonus.toFixed(2),
        travelSavings: travelSavings.toFixed(2),
        creditValue: creditValue.toFixed(2),
        foreignFeeSavings: foreignFeeSavings.toFixed(2),
        otherBenefits: otherBenefits.toFixed(2),
        insuranceValue: insuranceValue.toFixed(2),
        totalBenefits: totalBenefits.toFixed(2),
        netValue: netValue.toFixed(2),
        breakEvenSpending: breakEvenSpending.toFixed(2),
        valueRating,
        recommendations,
        rewardsBreakdown,
        pointsMultiplier: pointsMultiplier.toFixed(2),
        cardType: card.cardType
      };
    });

    // Calculate scenario comparison
    calculateScenarioComparison(cards, monthlySpending, travelProfile, timeframe);

    setResults(cardResults);
  };

  const calculateSignUpBonus = (card) => {
    const bonusPoints = parseFloat(card.signUpBonus) || 0;
    const baseValue = bonusPoints * 0.01; // 1 point = 1 cent base value
    
    // Adjust for spending requirement
    const spendRequirement = parseFloat(card.bonusSpendRequirement) || 0;
    const timeframe = parseFloat(card.bonusTimeframe) || 3;
    
    // Calculate opportunity cost of meeting spend requirement
    const monthlySpendForBonus = spendRequirement / timeframe;
    const opportunityCost = monthlySpendForBonus * 0.02 * timeframe; // Assuming 2% baseline rewards
    
    return Math.max(0, baseValue - opportunityCost);
  };

  const calculateTravelSavings = (card, travelProfile) => {
    let savings = 0;
    
    // Lounge access value
    if (card.loungeAccess !== "No") {
      const internationalTrips = parseFloat(travelProfile.internationalTrips) || 0;
      const domesticTrips = parseFloat(travelProfile.domesticTrips) || 0;
      savings += (internationalTrips * 50) + (domesticTrips * 30); // Estimated lounge value
    }

    // Travel credits
    const travelCredits = parseFloat(card.travelCredits) || 0;
    savings += travelCredits;

    // Priority Pass value
    if (card.priorityPass === "Yes") {
      savings += 429; // Annual value of Priority Pass
    }

    return savings;
  };

  const calculateCreditValue = (card) => {
    let total = 0;
    Object.values(card.credits || {}).forEach(credit => {
      total += parseFloat(credit) || 0;
    });
    return total;
  };

  const calculateForeignFeeSavings = (card, travelSpending, foreignPercent) => {
    const monthlyTravel = parseFloat(travelSpending) || 0;
    const foreignSpendingPercent = parseFloat(foreignPercent) || 0;
    const foreignSpending = monthlyTravel * (foreignSpendingPercent / 100) * 12;
    const foreignFee = parseFloat(card.foreignFee) || 0;
    
    return (foreignSpending * foreignFee) / 100;
  };

  const calculateBreakEvenSpending = (card, timeframe) => {
    const annualFee = parseFloat(card.annualFee) || 0;
    const signUpBonus = calculateSignUpBonus(card) * (timeframe === "first_year" ? 1 : 0);
    const otherBenefits = (parseFloat(card.otherBenefits) || 0) + 
                         (parseFloat(card.insuranceBenefits) || 0) +
                         calculateTravelSavings(card, travelProfile) +
                         calculateCreditValue(card);
    
    const effectiveRate = Object.values(card.rewardsRates).reduce((sum, rate) => 
      sum + parseFloat(rate), 0) / Object.keys(card.rewardsRates).length;
    
    const pointsMultiplier = parseFloat(card.pointsValue) || 1;
    const netFee = annualFee - signUpBonus - otherBenefits;
    
    if (netFee <= 0) return 0;
    
    return (netFee / (effectiveRate * pointsMultiplier / 100)) * (timeframe === "lifetime" ? 5 : 1);
  };

  const calculateValueRating = (netValue, annualFee, timeframe) => {
    const valueRatio = parseFloat(netValue) / parseFloat(annualFee);
    
    if (valueRatio >= 3) return "Excellent";
    if (valueRatio >= 2) return "Very Good";
    if (valueRatio >= 1) return "Good";
    if (valueRatio >= 0) return "Fair";
    return "Poor";
  };

  const generateRecommendations = (card, netValue, annualFee, timeframe) => {
    const recommendations = [];
    const netValueNum = parseFloat(netValue);
    const annualFeeNum = parseFloat(annualFee);

    if (netValueNum < 0) {
      recommendations.push({
        type: "critical",
        text: `This card has negative value. Consider downgrading or canceling to save $${Math.abs(netValueNum).toFixed(2)}`
      });
    }

    if (netValueNum > 0 && netValueNum < annualFeeNum * 0.5) {
      recommendations.push({
        type: "warning",
        text: "Marginal value - ensure you're maximizing all credits and benefits"
      });
    }

    if (parseFloat(card.travelCredits) > 0) {
      recommendations.push({
        type: "info",
        text: `Remember to use your $${card.travelCredits} travel credit annually`
      });
    }

    const unusedCredits = Object.entries(card.credits || {}).filter(([_, value]) => 
      parseFloat(value) > 0
    );
    if (unusedCredits.length > 0) {
      recommendations.push({
        type: "info",
        text: `Maximize value by using: ${unusedCredits.map(([name]) => name).join(', ')} credits`
      });
    }

    if (timeframe === "first_year" && parseFloat(card.signUpBonus) > 0) {
      recommendations.push({
        type: "success",
        text: "Great first-year value due to sign-up bonus - consider long-term value for renewal"
      });
    }

    return recommendations;
  };

  const calculateScenarioComparison = (cards, spending, travelProfile, timeframe) => {
    const scenarios = [
      { name: "Current Spending", multiplier: 1 },
      { name: "20% Less Spending", multiplier: 0.8 },
      { name: "20% More Spending", multiplier: 1.2 },
      { name: "50% More Travel", multiplier: 1, travelMultiplier: 1.5 },
      { name: "No International Travel", multiplier: 1, travelMultiplier: 0 }
    ];

    const scenarioResults = scenarios.map(scenario => {
      const adjustedSpending = { ...spending };
      Object.keys(adjustedSpending).forEach(category => {
        adjustedSpending[category] = (parseFloat(adjustedSpending[category]) || 0) * scenario.multiplier;
      });

      const adjustedTravelProfile = { ...travelProfile };
      if (scenario.travelMultiplier) {
        adjustedTravelProfile.internationalTrips = (parseFloat(travelProfile.internationalTrips) || 0) * scenario.travelMultiplier;
      }

      const cardResults = cards.map(card => {
        // Simplified calculation for scenario comparison
        const annualFee = parseFloat(card.annualFee) || 0;
        let annualRewards = 0;
        
        Object.entries(adjustedSpending).forEach(([category, amount]) => {
          const rate = card.rewardsRates[category] || card.rewardsRates.others || 1;
          annualRewards += ((parseFloat(amount) || 0) * parseFloat(rate) * 12) / 100;
        });

        const pointsMultiplier = parseFloat(card.pointsValue) || 1;
        const effectiveRewards = annualRewards * pointsMultiplier;
        const signUpBonus = timeframe === "first_year" ? calculateSignUpBonus(card) * pointsMultiplier : 0;
        const travelSavings = calculateTravelSavings(card, adjustedTravelProfile);
        const creditValue = calculateCreditValue(card);
        const foreignFeeSavings = calculateForeignFeeSavings(card, adjustedSpending.travel, adjustedTravelProfile.foreignSpendingPercent);

        const totalBenefits = effectiveRewards + signUpBonus + travelSavings + creditValue + foreignFeeSavings;
        const netValue = totalBenefits - annualFee;

        return {
          name: card.name,
          netValue: netValue.toFixed(2),
          valueRating: calculateValueRating(netValue, annualFee, timeframe)
        };
      });

      return {
        name: scenario.name,
        cards: cardResults
      };
    });

    setScenarioComparison(scenarioResults);
  };

  const printResults = () => {
    window.print();
  };

  const generatePDF = () => {
    alert("PDF generation would be implemented here with a library like jsPDF");
    window.print();
  };

  // Chart data configurations
  const valueBreakdownData = results ? {
    labels: results.map(res => res.name),
    datasets: [
      {
        label: 'Annual Rewards',
        data: results.map(res => parseFloat(res.annualRewards)),
        backgroundColor: '#F06201',
        borderWidth: 2,
        borderColor: '#fff',
      },
      {
        label: 'Sign-up Bonus',
        data: results.map(res => timeframe === "first_year" ? parseFloat(res.signUpBonus) : 0),
        backgroundColor: '#FF8C42',
        borderWidth: 2,
        borderColor: '#fff',
      },
      {
        label: 'Travel & Credits',
        data: results.map(res => parseFloat(res.travelSavings) + parseFloat(res.creditValue)),
        backgroundColor: '#A03B00',
        borderWidth: 2,
        borderColor: '#fff',
      },
      {
        label: 'Other Benefits',
        data: results.map(res => parseFloat(res.otherBenefits) + parseFloat(res.insuranceValue) + parseFloat(res.foreignFeeSavings)),
        backgroundColor: '#FFB088',
        borderWidth: 2,
        borderColor: '#fff',
      }
    ],
  } : null;

  const netValueComparisonData = results ? {
    labels: results.map(res => res.name),
    datasets: [
      {
        label: 'Total Benefits',
        data: results.map(res => parseFloat(res.totalBenefits)),
        backgroundColor: '#38a169',
        borderWidth: 2,
        borderColor: '#fff',
      },
      {
        label: 'Annual Fee',
        data: results.map(res => -parseFloat(res.annualFee)),
        backgroundColor: '#A03B00',
        borderWidth: 2,
        borderColor: '#fff',
      },
      {
        label: 'Net Value',
        data: results.map(res => parseFloat(res.netValue)),
        backgroundColor: results.map(res => 
          parseFloat(res.netValue) > 0 ? '#F06201' : '#e53e3e'
        ),
        borderWidth: 2,
        borderColor: '#fff',
        type: 'line',
        yAxisID: 'y1'
      }
    ],
  } : null;

  const rewardsDistributionData = results && results[0] ? {
    labels: Object.keys(results[0].rewardsBreakdown || {}),
    datasets: [
      {
        label: 'Annual Rewards by Category ($)',
        data: Object.values(results[0].rewardsBreakdown || {}),
        backgroundColor: ['#F06201', '#FF8C42', '#A03B00', '#FFB088', '#FFD1B8', '#38a169', '#68d391', '#f6e05e', '#ed8936', '#e53e3e'],
        borderWidth: 2,
        borderColor: '#fff',
      }
    ],
  } : null;

  const scenarioComparisonData = scenarioComparison ? {
    labels: scenarioComparison.map(scenario => scenario.name),
    datasets: cards.map((card, index) => ({
      label: card.name,
      data: scenarioComparison.map(scenario => 
        parseFloat(scenario.cards[index]?.netValue || 0)
      ),
      backgroundColor: index === 0 ? '#F06201' : 
                      index === 1 ? '#FF8C42' : 
                      index === 2 ? '#A03B00' : '#FFB088',
      borderWidth: 2,
      borderColor: '#fff',
    }))
  } : null;

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
  };

  const netValueChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Amount ($)'
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'Net Value ($)'
        },
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  const totalMonthlySpending = Object.values(monthlySpending).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);

  return (
    <div className="calculator-container">
      <header className="calculator-header">
        <div className="header-content">
          <h1>Annual Fee Value Calculator</h1>
          <p>Calculate the true value of premium credit cards and optimize your card portfolio</p>
          <div className="debt-summary">
            <div className="summary-item">
              <span>Total Monthly Spending:</span>
              <strong>${totalMonthlySpending.toFixed(2)}</strong>
            </div>
            <div className="summary-item">
              <span>Cards Analyzed:</span>
              <strong>{cards.length}</strong>
            </div>
            <div className="summary-item">
              <span>Timeframe:</span>
              <strong>
                {timeframe === "first_year" ? "First Year" : 
                 timeframe === "ongoing" ? "Ongoing" : "5 Years"}
              </strong>
            </div>
            {results && results[0] && (
              <div className="summary-item">
                <span>Best Value:</span>
                <strong className={parseFloat(results[0].netValue) > 0 ? "savings" : "warning"}>
                  ${results[0].netValue}
                </strong>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Tabs Navigation */}
      <div className="mobile-tabs">
        <button 
          className={`tab-btn ${activeTab === 'calculator' ? 'active' : ''}`}
          onClick={() => setActiveTab('calculator')}
        >
          <i className="fa-solid fa-calculator"></i>
          Calculator
        </button>
        <button 
          className={`tab-btn ${activeTab === 'results' ? 'active' : ''}`}
          onClick={() => setActiveTab('results')}
        >
          <i className="fa-solid fa-chart-pie"></i>
          Results
        </button>
      </div>

      <div className="calculator-layout">
        {/* Left Side - Calculator Inputs */}
        <div className={`left-panel ${activeTab === 'calculator' ? 'active' : ''}`}>
          <div className="cards-section">
            <div className="section-header">
              <h2>Card Analysis</h2>
              <div className="card-actions">
                <button className="btn-secondary" onClick={addCard}>
                  <i className="fa-solid fa-plus"></i> Add Card
                </button>
                <button className="btn-reset" onClick={resetCalculator}>
                  Reset All
                </button>
              </div>
            </div>

            <div className="strategy-selector">
              <div className="strategy-info">
                <i className="fa-solid fa-lightbulb"></i>
                {" "}Calculate the true value of premium credit cards by considering all benefits, credits, and your spending patterns
              </div>
            </div>

            {/* Timeframe Selector */}
            <div className="timeframe-selector">
              <div className="input-group">
                <label>Analysis Timeframe</label>
                <select 
                  value={timeframe} 
                  onChange={(e) => setTimeframe(e.target.value)}
                >
                  <option value="first_year">First Year (with sign-up bonus)</option>
                  <option value="ongoing">Ongoing (after first year)</option>
                  <option value="lifetime">5-Year Lifetime Value</option>
                </select>
              </div>
            </div>

            {/* Card Navigation Tabs */}
            {cards.length > 1 && (
              <div className="card-navigation">
                {cards.map((card, index) => (
                  <button
                    key={card.id}
                    className={`nav-tab ${activeCardIndex === index ? 'active' : ''}`}
                    onClick={() => setActiveCardIndex(index)}
                  >
                    {card.name}
                  </button>
                ))}
              </div>
            )}

            {/* Monthly Spending */}
            <br/><div className="spending-section">
              <h3>Monthly Spending Profile</h3>
              <div className="input-grid">
                {Object.entries(monthlySpending).map(([category, amount]) => (
                  <div key={category} className="input-group">
                    <label>{category.charAt(0).toUpperCase() + category.slice(1)} ($)</label>
                    <input
                      type="number"
                      value={amount}
                      onChange={(e) => handleSpendingChange(category, e.target.value)}
                      placeholder="0"
                      min="0"
                      step="0.01"
                    />
                  </div>
                ))}
              </div>
            </div><br/>

            {/* Travel Profile */}
            <div className="travel-section">
              <h3>Travel Profile</h3>
              <div className="input-grid">
                <div className="input-group">
                  <label>International Trips/Year</label>
                  <input
                    type="number"
                    value={travelProfile.internationalTrips}
                    onChange={(e) => handleTravelProfileChange("internationalTrips", e.target.value)}
                    placeholder="2"
                    min="0"
                  />
                </div>
                <div className="input-group">
                  <label>Domestic Trips/Year</label>
                  <input
                    type="number"
                    value={travelProfile.domesticTrips}
                    onChange={(e) => handleTravelProfileChange("domesticTrips", e.target.value)}
                    placeholder="4"
                    min="0"
                  />
                </div>
                <div className="input-group">
                  <label>Hotel Nights/Year</label>
                  <input
                    type="number"
                    value={travelProfile.hotelNights}
                    onChange={(e) => handleTravelProfileChange("hotelNights", e.target.value)}
                    placeholder="10"
                    min="0"
                  />
                </div>
                <div className="input-group">
                  <label>Rental Car Days/Year</label>
                  <input
                    type="number"
                    value={travelProfile.rentalCarDays}
                    onChange={(e) => handleTravelProfileChange("rentalCarDays", e.target.value)}
                    placeholder="5"
                    min="0"
                  />
                </div>
                <div className="input-group">
                  <label>Foreign Spending (%)</label>
                  <input
                    type="number"
                    value={travelProfile.foreignSpendingPercent}
                    onChange={(e) => handleTravelProfileChange("foreignSpendingPercent", e.target.value)}
                    placeholder="25"
                    min="0"
                    max="100"
                  />
                </div>
              </div>
            </div><br/>

            <div className="cards-container">
              {cards.map((card, index) => (
                <div 
                  key={card.id} 
                  className={`card-input-group ${activeCardIndex === index ? 'active' : ''}`}
                >
                  <div className="card-header">
                    <h3>{card.name}</h3>
                    {cards.length > 1 && (
                      <button 
                        className="btn-remove" 
                        onClick={() => removeCard(card.id)}
                        title="Remove card"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  
                  <div className="input-grid">
                    <div className="input-group">
                      <label>Card Name*</label>
                      <input
                        type="text"
                        value={card.name}
                        onChange={(e) => handleCardChange(card.id, "name", e.target.value)}
                        placeholder="Card Name"
                      />
                    </div>
                    <div className="input-group">
                      <label>Issuer</label>
                      <select
                        value={card.issuer}
                        onChange={(e) => handleCardChange(card.id, "issuer", e.target.value)}
                      >
                        <option value="Chase">Chase</option>
                        <option value="American Express">American Express</option>
                        <option value="Bank of America">Bank of America</option>
                        <option value="Citi">Citi</option>
                        <option value="Capital One">Capital One</option>
                        <option value="Discover">Discover</option>
                      </select>
                    </div>
                    <div className="input-group">
                      <label>Annual Fee ($)*</label>
                      <input
                        type="number"
                        value={card.annualFee}
                        onChange={(e) => handleCardChange(card.id, "annualFee", e.target.value)}
                        placeholder="95"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div className="input-group">
                      <label>Sign-up Bonus (Points)</label>
                      <input
                        type="number"
                        value={card.signUpBonus}
                        onChange={(e) => handleCardChange(card.id, "signUpBonus", e.target.value)}
                        placeholder="60000"
                        min="0"
                      />
                    </div>
                    <div className="input-group">
                      <label>Bonus Spend Requirement ($)</label>
                      <input
                        type="number"
                        value={card.bonusSpendRequirement}
                        onChange={(e) => handleCardChange(card.id, "bonusSpendRequirement", e.target.value)}
                        placeholder="4000"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div className="input-group">
                      <label>Bonus Timeframe (Months)</label>
                      <input
                        type="number"
                        value={card.bonusTimeframe}
                        onChange={(e) => handleCardChange(card.id, "bonusTimeframe", e.target.value)}
                        placeholder="3"
                        min="1"
                        max="12"
                      />
                    </div>
                    <div className="input-group">
                      <label>Points Value Multiplier</label>
                      <input
                        type="number"
                        value={card.pointsValue}
                        onChange={(e) => handleCardChange(card.id, "pointsValue", e.target.value)}
                        placeholder="1.25"
                        min="1"
                        max="2"
                        step="0.01"
                      />
                    </div>
                    <div className="input-group">
                      <label>Foreign Transaction Fee (%)</label>
                      <input
                        type="number"
                        value={card.foreignFee}
                        onChange={(e) => handleCardChange(card.id, "foreignFee", e.target.value)}
                        placeholder="0"
                        min="0"
                        max="10"
                        step="0.01"
                      />
                    </div>
                    <div className="input-group">
                      <label>Travel Credits ($)</label>
                      <input
                        type="number"
                        value={card.travelCredits}
                        onChange={(e) => handleCardChange(card.id, "travelCredits", e.target.value)}
                        placeholder="0"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div className="input-group">
                      <label>Other Benefits Value ($)</label>
                      <input
                        type="number"
                        value={card.otherBenefits}
                        onChange={(e) => handleCardChange(card.id, "otherBenefits", e.target.value)}
                        placeholder="150"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div className="input-group">
                      <label>Insurance Benefits Value ($)</label>
                      <input
                        type="number"
                        value={card.insuranceBenefits}
                        onChange={(e) => handleCardChange(card.id, "insuranceBenefits", e.target.value)}
                        placeholder="250"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>

                  {/* Rewards Rates */}
                  <div className="cashback-section">
                    <h4>Rewards Rates (%)</h4>
                    <div className="input-grid">
                      {Object.entries(card.rewardsRates).map(([category, rate]) => (
                        <div key={category} className="input-group">
                          <label>{category.charAt(0).toUpperCase() + category.slice(1)} (%)</label>
                          <input
                            type="number"
                            value={rate}
                            onChange={(e) => handleCardChange(card.id, "rewardsRates", e.target.value, category)}
                            placeholder="1"
                            min="0"
                            max="100"
                            step="0.01"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Monthly Credits */}
                  <div className="credits-section">
                    <h4>Monthly/Annual Credits ($)</h4>
                    <div className="input-grid">
                      {Object.entries(card.credits || {}).map(([credit, value]) => (
                        <div key={credit} className="input-group">
                          <label>{credit.charAt(0).toUpperCase() + credit.slice(1)} Credit</label>
                          <input
                            type="number"
                            value={value}
                            onChange={(e) => handleCardChange(card.id, "credits", e.target.value, credit)}
                            placeholder="0"
                            min="0"
                            step="0.01"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="required-note">* Required fields</div>
                </div>
              ))}
            </div>
          </div>

          <div className="actions-section">
            <button className="btn-primary" onClick={calculateValue}>
              Calculate Card Value
            </button>
          </div>

          {/* Default tips section */}
          <div className="default-results">
            <h2>Annual Fee Optimization Guide</h2>
            <div className="default-message">
              <p>Analyze whether premium credit cards are worth their annual fees based on your spending and travel patterns.</p>
              <div className="default-tips">
                <h3><i className="fa-solid fa-lightbulb"></i> Value Optimization Tips:</h3>
                <ul>
                  <li>Calculate both first-year value (with sign-up bonus) and ongoing value</li>
                  <li>Consider all credits, lounge access, insurance, and other benefits</li>
                  <li>Match card categories with your highest spending areas</li>
                  <li>Factor in foreign transaction fees if you travel internationally</li>
                  <li>Calculate break-even spending to see if the card fits your budget</li>
                  <li>Consider product change options before canceling valuable cards</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Results */}
        <div className={`right-panel ${activeTab === 'results' ? 'active' : ''}`} ref={resultsRef}>
          {results && results.length > 0 ? (
            <div className="results-section">
              <div className="section-header">
                <h2>Annual Fee Value Analysis</h2>
                <div className="results-actions">
                  <div className="chart-toggle">
                    <button 
                      className={`toggle-btn ${showChart ? 'active' : ''}`}
                      onClick={() => setShowChart(true)}
                    >
                      <i className="fa-solid fa-chart-column"></i> Charts
                    </button>
                    <button 
                      className={`toggle-btn ${!showChart ? 'active' : ''}`}
                      onClick={() => setShowChart(false)}
                    >
                      <i className="fa-solid fa-clipboard-list"></i> Details
                    </button>
                  </div>
                  <div className="button-group">
                    <button className="btn-secondary" onClick={printResults}>
                      <i className="fa-solid fa-print"></i> Print
                    </button>
                    <button className="btn-secondary" onClick={generatePDF}>
                      <i className="fa-regular fa-file-lines"></i> Save as PDF
                    </button>
                  </div>
                </div>
              </div>

              {/* Results Summary */}
              <div className="results-summary">
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-coins"></i></div>
                  <h3>Net Value</h3>
                  <div className={`summary-value ${parseFloat(results[0].netValue) > 0 ? "savings" : "warning"}`}>
                    ${results[0].netValue}
                  </div>
                  <div className="summary-note">
                    {timeframe === "first_year" ? "First Year" : 
                     timeframe === "ongoing" ? "Annual" : "5-Year Total"}
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-star"></i></div>
                  <h3>Value Rating</h3>
                  <div className={`summary-value ${
                    results[0].valueRating === "Excellent" ? "savings" :
                    results[0].valueRating === "Very Good" ? "" :
                    results[0].valueRating === "Good" ? "" : "warning"
                  }`}>
                    {results[0].valueRating}
                  </div>
                  <div className="summary-note">Card worthiness</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-percentage"></i></div>
                  <h3>Return on Fee</h3>
                  <div className="summary-value">
                    {((parseFloat(results[0].netValue) / parseFloat(results[0].annualFee) * 100) || 0).toFixed(0)}%
                  </div>
                  <div className="summary-note">Benefits vs cost</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-bullseye"></i></div>
                  <h3>Break-even Spending</h3>
                  <div className="summary-value">${results[0].breakEvenSpending}</div>
                  <div className="summary-note">
                    {timeframe === "lifetime" ? "5-year total" : "Annual"}
                  </div>
                </div>
              </div>

              {showChart ? (
                <div className="charts-grid">
                  <div className="chart-card">
                    <h3>Value Breakdown</h3>
                    {valueBreakdownData && <Bar data={valueBreakdownData} options={chartOptions} />}
                    <div className="chart-description">
                      <p><strong>Benefits Analysis:</strong> See how different benefits contribute to the total card value.</p>
                    </div>
                  </div>
                  
                  <div className="chart-card">
                    <h3>Net Value Comparison</h3>
                    {netValueComparisonData && <Bar data={netValueComparisonData} options={netValueChartOptions} />}
                    <div className="chart-description">
                      <p><strong>Cost-Benefit Analysis:</strong> Compare total benefits against annual fees to see net value.</p>
                    </div>
                  </div>

                  <div className="chart-card">
                    <h3>Rewards Distribution</h3>
                    {rewardsDistributionData && <Doughnut data={rewardsDistributionData} options={chartOptions} />}
                    <div className="chart-description">
                      <p><strong>Spending Alignment:</strong> See how your spending categories align with the card's rewards structure.</p>
                    </div>
                  </div>

                  {scenarioComparison && (
                    <div className="chart-card">
                      <h3>Scenario Analysis</h3>
                      {scenarioComparisonData && <Bar data={scenarioComparisonData} options={chartOptions} />}
                      <div className="chart-description">
                        <p><strong>Sensitivity Analysis:</strong> See how changes in spending affect card value across different scenarios.</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="results-details">
                  <div className="results-grid">
                    {results.map((result, index) => (
                      <div key={index} className="result-card">
                        <h3>{result.name} - Value Analysis</h3>
                        <div className="result-stats">
                          <div className="stat">
                            <label>Annual Fee</label>
                            <span className="stat-value">-${result.annualFee}</span>
                          </div>
                          <div className="stat">
                            <label>Annual Rewards</label>
                            <span className="stat-value">+${result.annualRewards}</span>
                          </div>
                          <div className="stat">
                            <label>Sign-up Bonus</label>
                            <span className="stat-value savings">+${result.signUpBonus}</span>
                          </div>
                          <div className="stat">
                            <label>Travel & Credits</label>
                            <span className="stat-value">+${(parseFloat(result.travelSavings) + parseFloat(result.creditValue)).toFixed(2)}</span>
                          </div>
                          <div className="stat">
                            <label>Foreign Fee Savings</label>
                            <span className="stat-value">+${result.foreignFeeSavings}</span>
                          </div>
                          <div className="stat">
                            <label>Other Benefits</label>
                            <span className="stat-value">+${(parseFloat(result.otherBenefits) + parseFloat(result.insuranceValue)).toFixed(2)}</span>
                          </div>
                          <div className="stat">
                            <label>Total Benefits</label>
                            <span className="stat-value savings">+${result.totalBenefits}</span>
                          </div>
                          <div className="stat">
                            <label>Net Value</label>
                            <span className={`stat-value ${parseFloat(result.netValue) > 0 ? "savings" : "warning"}`}>
                              ${result.netValue}
                            </span>
                          </div>
                          <div className="stat">
                            <label>Value Rating</label>
                            <span className={`stat-value ${
                              result.valueRating === "Excellent" ? "savings" :
                              result.valueRating === "Very Good" ? "" :
                              result.valueRating === "Good" ? "" : "warning"
                            }`}>
                              {result.valueRating}
                            </span>
                          </div>
                          <div className="stat">
                            <label>Break-even Spending</label>
                            <span className="stat-value">${result.breakEvenSpending}</span>
                          </div>
                          <div className="stat">
                            <label>Points Multiplier</label>
                            <span className="stat-value">{result.pointsMultiplier}x</span>
                          </div>
                        </div>

                        {/* Rewards Breakdown */}
                        <div className="monthly-breakdown">
                          <h4>Rewards by Category</h4>
                          <div className="breakdown-table">
                            <table>
                              <thead>
                                <tr>
                                  <th>Category</th>
                                  <th>Annual Rewards</th>
                                  <th>Your Spending</th>
                                  <th>Rewards Rate</th>
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(result.rewardsBreakdown || {}).map(([category, rewards]) => (
                                  <tr key={category}>
                                    <td>{category.charAt(0).toUpperCase() + category.slice(1)}</td>
                                    <td>${parseFloat(rewards).toFixed(2)}</td>
                                    <td>${((parseFloat(monthlySpending[category]) || 0) * 12).toFixed(2)}</td>
                                    <td>{cards[index].rewardsRates[category] || cards[index].rewardsRates.others}%</td>
                                  </tr>
                                ))}
                                <tr>
                                  <td><strong>Total</strong></td>
                                  <td><strong>${result.annualRewards}</strong></td>
                                  <td><strong>${(totalMonthlySpending * 12).toFixed(2)}</strong></td>
                                  <td></td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Scenario Comparison */}
                  {scenarioComparison && (
                    <div className="scenario-comparison">
                      <h3>Scenario Analysis</h3>
                      <div className="scenario-grid">
                        {scenarioComparison.map((scenario, index) => (
                          <div key={index} className="scenario-card">
                            <h4>{scenario.name}</h4>
                            <div className="scenario-stats">
                              {scenario.cards.map((card, cardIndex) => (
                                <div key={cardIndex} className="stat">
                                  <label>{card.name}</label>
                                  <span className={parseFloat(card.netValue) > 0 ? "savings" : "warning"}>
                                    ${card.netValue} ({card.valueRating})
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actionable Recommendations */}
                  <div className="default-results">
                    <h3>Action Plan</h3>
                    <div className="recommendations-list">
                      {results[0].recommendations.map((rec, i) => (
                        <div key={i} className={`default-tips ${rec.type}`}>
                          <i className={`fa-solid ${
                            rec.type === 'critical' ? 'fa-exclamation-triangle' :
                            rec.type === 'warning' ? 'fa-exclamation-circle' :
                            rec.type === 'success' ? 'fa-check-circle' : 'fa-info-circle'
                          }`}></i>
                          <span>{rec.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="no-results">
              <h3>No results yet</h3>
              <p>Enter card details and your spending profile to calculate annual fee value.</p>
            </div>
          )}
        </div>
      </div>
      {content && content.content}
    </div>
  );
};

export default AnnualFeeValueCalculator;